/**
 * DESEMPENHO INDIVIDUAL POR INSPETOR (Painel → Dashboard).
 *
 * Função pura, no mesmo espírito de `dashboard-indicators.ts`: recebe os
 * dados reais já lidos e devolve o recorte por pessoa. Nenhum número é
 * estimado; onde o dado não existe, o bloco declara que não é calculável.
 *
 * ── DE ONDE VEM A ATRIBUIÇÃO ──────────────────────────────────────────────
 *
 * A pessoa é sempre o **Professional**, nunca o `createdBy`/`updatedBy` do
 * registro. Os dois campos respondem perguntas diferentes: `updatedBy` é o
 * usuário que digitou por último, e o Controle de Campo declara
 * explicitamente que o avanço pertence à LINHA, não ao Inspetor — dois
 * Inspetores vinculados ao mesmo Sistema alteram o MESMO número
 * (`field-inspection-progress.ts`). Medir desempenho por quem salvou por
 * último atribuiria o trabalho inteiro a quem clicou depois.
 *
 * O escopo individual tem, portanto, duas fontes reais e distintas:
 *
 *   CAMPO      `FieldInspectionAssignment.inspectorProfessionalId → systemId`
 *              → as Linhas dos Sistemas atribuídos àquele Inspetor.
 *   INSPEÇÕES  `Inspection.inspectorId` — o Professional responsável pela
 *              inspeção, campo do próprio domínio.
 *
 * ── LINHA FÍSICA ÚNICA ────────────────────────────────────────────────────
 *
 * A contagem é sempre por `lineEquipmentId` DISTINTO. A mesma Linha pode
 * chegar por mais de um caminho (dois vínculos ao mesmo Sistema, linha
 * repetida no apontamento) e contá-la duas vezes inflaria tanto o total
 * quanto o avanço médio da pessoa.
 *
 * ── ESCOPO COMPARTILHADO ──────────────────────────────────────────────────
 *
 * Um Sistema pode ter mais de um Inspetor. Nesse caso a mesma Linha entra no
 * escopo dos dois — é o que o modelo diz, e é a realidade da operação. A
 * consequência é declarada (`sharedLines`, `overlapping`) em vez de
 * escondida: somar as Linhas de todos os Inspetores pode passar do total de
 * Linhas distintas, e quem lê precisa saber por quê.
 */

import { dayOf, linesAssignedTo, OFFSITE_TUBOVIA_GROUP_ID, OFFSITE_TUBOVIA_GROUP_LABEL, systemsAssignedTo, withinPeriod, type DashboardAssignment, type DashboardFieldProgress, type DashboardFilters } from "./dashboard-indicators.js";

/** Vínculo Inspetor → Sistema. Um só tipo para o Painel inteiro. */
export type InspectorAssignment = DashboardAssignment;

/**
 * Texto oficial exibido quando a pessoa não tem escopo individual atribuído.
 * Zero por cento seria uma afirmação sobre o trabalho dela; "não calculável"
 * é uma afirmação sobre o cadastro, que é a verdade.
 */
export const NO_INDIVIDUAL_SCOPE = "NÃO CALCULÁVEL SEM ESCOPO ATRIBUÍDO";

/** Inspeção atribuída a um Professional (`Inspection.inspectorId`). */
export interface InspectorInspection {
  id: string;
  inspectorProfessionalId?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  updatedAt?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
}

export interface InspectorPerson {
  id: string;
  name?: string | null;
}

export interface InspectorPerformanceInput {
  /** Profissionais candidatos a Inspetor. Só entram os que o chamador já filtrou. */
  professionals: readonly InspectorPerson[];
  assignments: readonly InspectorAssignment[];
  fieldProgress: readonly DashboardFieldProgress[];
  inspections?: readonly InspectorInspection[];
  /** systemId → TAG do Sistema, para o detalhamento ficar legível. */
  systemLabels?: ReadonlyMap<string, string>;
  /**
   * REGRA V4-COMPLEMENTO: lineEquipmentId → TAG real da Linha/Tubulação.
   * Usado para drill-down no breakdown por Inspetor (Sistemas e OFF-SITE).
   * Ausente ⇒ usa lineEquipmentId como fallback.
   */
  lineLabels?: ReadonlyMap<string, string>;
  /**
   * Dias trabalhados apontados (RDO). Ausente ⇒ cada pessoa cai no derivado
   * das conclusões, e a tela diz que é derivado.
   */
  workedDays?: WorkedDaysIndex;
}

/**
 * DE ONDE VEM O DENOMINADOR DA MÉDIA.
 *
 * `RDO` é o registro real: a **quantidade de RDOs criados no período** pela
 * pessoa. Cada apontamento é um dia de trabalho declarado por ela, **tenha
 * ou não concluído alguma Linha naquele dia**.
 *
 * `CONCLUSOES` é o derivado — dias DISTINTOS em que houve conclusão. Serve
 * quando ainda não há RDO apontado, mas **superestima o ritmo**: só enxerga
 * os dias produtivos. Quem concluiu 20 Linhas em 3 dias de um mês de 10
 * apontamentos sai a 6,7 pelo derivado e a 2,0 pelo RDO. O segundo é o
 * verdadeiro, e é por isso que a fonte usada é sempre declarada na tela.
 */
export const WorkedDaysSource = {
  Rdo: "RDO",
  Completions: "CONCLUSOES",
  None: "NENHUMA"
} as const;
export type WorkedDaysSource = (typeof WorkedDaysSource)[keyof typeof WorkedDaysSource];

/** O que o RDO de uma pessoa rendeu no recorte. */
export interface ReportedWork {
  /**
   * QUANTIDADE DE RDOs criados no período — o denominador da média.
   * Não é o mesmo que dias distintos: a pessoa pode apontar mais de um RDO
   * no mesmo dia (Frentes ou turnos diferentes), e os dois números são
   * mostrados lado a lado quando divergem.
   */
  entries: number;
  /** Dias DISTINTOS cobertos por esses RDOs. */
  days: ReadonlySet<string>;
  /** Horas somadas dos apontamentos contados. */
  hours: number;
}

/** Trabalho apontado por pessoa: `professionalId` → RDOs do recorte. */
export interface WorkedDaysIndex {
  reportedOf(professionalId: string): ReportedWork | undefined;
}

/** Apontamento diário mínimo lido do RDO. */
export interface WorkedDayEntry {
  professionalId: string;
  /** AAAA-MM-DD. */
  date: string;
  status?: string | null;
  deletedAt?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  normalHours?: number | null;
  overtimeHours?: number | null;
}

/**
 * Índice de dias trabalhados a partir dos apontamentos de RDO.
 *
 * Exclui **apenas** `CANCELLED` e registros apagados — a mesma regra que a
 * consolidação do RDO já aplica (`rdo-consolidation.ts:149`). Duas definições
 * de "dia apontado" no mesmo sistema divergiriam com o tempo, e o número do
 * Painel deixaria de bater com o consolidado que a pessoa assina.
 *
 * O recorte de Projeto / Unidade / Período do Painel vale aqui também: um dia
 * apontado noutro Projeto não é dia trabalhado NESTE recorte.
 */
export function workedDaysFromRdo(
  entries: readonly WorkedDayEntry[],
  filters: DashboardFilters = {}
): WorkedDaysIndex {
  const byPerson = new Map<string, { entries: number; days: Set<string>; hours: number }>();
  for (const entry of entries) {
    if (entry.deletedAt) continue;
    if (text(entry.status).toUpperCase() === "CANCELLED") continue;
    const person = text(entry.professionalId);
    const day = dayOf(text(entry.date));
    if (!person || !day) continue;
    if (!matchesScope(entry, filters)) continue;
    if (!withinPeriod(day, filters)) continue;
    let bucket = byPerson.get(person);
    if (!bucket) { bucket = { entries: 0, days: new Set<string>(), hours: 0 }; byPerson.set(person, bucket); }
    // Cada RDO conta UM — é o que o denominador pede. O dia entra no Set,
    // que por definição não duplica: é assim que dois apontamentos no mesmo
    // dia viram 2 RDOs e 1 dia, e a divergência fica visível.
    bucket.entries += 1;
    bucket.days.add(day);
    bucket.hours += (entry.normalHours ?? 0) + (entry.overtimeHours ?? 0);
  }
  return { reportedOf: (professionalId: string) => byPerson.get(text(professionalId)) };
}

/**
 * RITMO DIÁRIO — média de Linhas concluídas por dia.
 *
 * O numerador é `FieldInspectionProgress.completedAt`, carimbado sozinho
 * quando a Linha chega a 100% e apagado se o percentual cair. Não existe uma
 * segunda data digitada que pudesse discordar do percentual.
 *
 * O denominador é o que a seção acima descreve: quantidade de RDOs quando há
 * apontamento, dias com conclusão quando não. **20 Linhas com 10 RDOs no
 * período = 2,0**, mesmo que as conclusões tenham se concentrado em 3 desses
 * dias — dia em campo sem Linha fechada é dia trabalhado e pertence ao
 * denominador.
 *
 * Duas médias, porque respondem perguntas diferentes:
 *
 *   POR DIA TRABALHADO   concluídas ÷ RDOs do período. É o ritmo da pessoa.
 *   POR DIA CORRIDO      concluídas ÷ dias entre a primeira e a última
 *                        conclusão. Conta os dias parados, e é o número que
 *                        serve para planejar prazo.
 */
export interface InspectorDailyRate {
  /** Concluídas COM data de conclusão dentro do recorte. */
  completedWithDate: number;
  /** Concluídas sem data registrada — ficam FORA da média e são declaradas. */
  completedWithoutDate: number;
  /**
   * O DENOMINADOR da média: quantidade de RDOs criados no período quando há
   * apontamento; dias distintos com conclusão quando não há. Ver `daysSource`.
   */
  daysWorked: number;
  /** De onde saiu o denominador: RDO apontado ou derivado das conclusões. */
  daysSource: WorkedDaysSource;
  /** Quantidade de RDOs criados no recorte (0 quando não há apontamento). */
  reportedEntries: number;
  /**
   * Dias DISTINTOS cobertos pelos RDOs. Difere de `reportedEntries` quando a
   * pessoa aponta mais de um RDO no mesmo dia — a tela mostra os dois.
   */
  reportedDays: number;
  /** Dias apontados no RDO em que NENHUMA Linha foi concluída. */
  daysWithoutCompletion: number;
  /** Horas apontadas nos apontamentos contados, quando o RDO as traz. */
  hoursWorked: number;
  /** Dias corridos da primeira à última conclusão, inclusive. */
  elapsedDays: number;
  /** Concluídas ÷ dias trabalhados, uma casa decimal. */
  linesPerWorkedDay: number;
  /** Concluídas ÷ dias corridos, uma casa decimal. */
  linesPerElapsedDay: number;
  firstDay: string | null;
  lastDay: string | null;
  /** Há conclusão datada suficiente para haver média. */
  hasData: boolean;
}

/**
 * REGRA V4-COMPLEMENTO: TAG real de uma Linha/Tubulação para drill-down.
 * Preserva dados reais do cadastro; NÃO é identificador fictício.
 */
export interface InspectorLineTag {
  lineEquipmentId: string;
  tag: string;
  clientId?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  status?: string | null;
  progressPercent: number;
  outOfOperation: boolean;
}

export interface InspectorSystemBreakdown {
  systemId: string;
  label: string;
  lines: number;
  operational: number;
  completed: number;
  pending: number;
  outOfOperation: number;
  progressPercent: number;
  /** Ritmo naquele Sistema — mostra de onde vem a média da pessoa. */
  dailyRate: InspectorDailyRate;
  /** O Sistema também está atribuído a outro Inspetor. */
  shared: boolean;
  /**
   * REGRA V4-COMPLEMENTO: TAGs reais das linhas deste grupo.
   * Para Sistemas reais: linhas daquele Sistema.
   * Para OFF-SITE/TUBOVIA: linhas sem Sistema atribuídas ao Inspetor.
   */
  lineTags?: InspectorLineTag[];
}

export interface InspectorPerformance {
  professionalId: string;
  name: string;
  /** Há escopo individual (vínculo de campo OU inspeção atribuída). */
  hasScope: boolean;
  /** `NO_INDIVIDUAL_SCOPE` quando não há escopo; `null` quando há. */
  unavailableReason: string | null;
  assignedSystems: number;
  /** Linhas FÍSICAS distintas — `lineEquipmentId` único. */
  totalLines: number;
  operationalLines: number;
  completedLines: number;
  pendingLines: number;
  outOfOperationLines: number;
  /** Média dos percentuais das Linhas operacionais, uma casa decimal. */
  progressPercent: number;
  /** Linhas do escopo que outro Inspetor também acompanha. */
  sharedLines: number;
  /** Média de Linhas concluídas por dia — o critério do ranking. */
  dailyRate: InspectorDailyRate;
  assignedInspections: number;
  completedInspections: number;
  /** Detalhamento por Sistema — a origem de cada número acima. */
  systems: InspectorSystemBreakdown[];
}

export interface InspectorPerformanceReport {
  hasData: boolean;
  inspectors: InspectorPerformance[];
  /** Linhas físicas distintas somando TODOS os escopos, sem duplicar. */
  distinctLinesInScope: number;
  /** Algum Sistema tem mais de um Inspetor — a soma por pessoa não fecha. */
  overlapping: boolean;
}

const COMPLETED_INSPECTION_STATUSES = new Set(["COMPLETED", "REPORT_ISSUED", "CLOSED", "FINISHED"]);

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));
const sameId = (a: unknown, b: unknown): boolean => text(a) !== "" && text(a) === text(b);

function matchesScope(item: { clientId?: string | null; projectId?: string | null; unitAreaId?: string | null }, filters: DashboardFilters): boolean {
  if (filters.clientId && !sameId(item.clientId, filters.clientId)) return false;
  if (filters.projectId && !sameId(item.projectId, filters.projectId)) return false;
  if (filters.unitAreaId && !sameId(item.unitAreaId, filters.unitAreaId)) return false;
  return true;
}

/**
 * Média dos percentuais das Linhas operacionais. Uma casa decimal pelo mesmo
 * motivo do painel geral: o número real cai no meio com frequência.
 */
function averageOf(lines: readonly DashboardFieldProgress[]): number {
  const operational = lines.filter(line => !line.outOfOperation);
  if (operational.length === 0) return 0;
  const sum = operational.reduce((total, line) => total + line.progressPercent, 0);
  return Math.round((sum / operational.length) * 10) / 10;
}

const oneDecimal = (value: number): number => Math.round(value * 10) / 10;

/** Dias corridos entre dois dias AAAA-MM-DD, contando as duas pontas. */
function inclusiveDaySpan(first: string, last: string): number {
  const start = Date.parse(`${first}T00:00:00Z`);
  const end = Date.parse(`${last}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 1;
  return Math.round((end - start) / 86_400_000) + 1;
}

/**
 * Ritmo diário de um conjunto de Linhas.
 *
 * Só entra Linha CONCLUÍDA e operacional: uma Linha Fora de Operação não foi
 * inspecionada, e uma em andamento ainda não tem dia de conclusão. Quando o
 * Painel tem recorte de Período, a conclusão precisa cair dentro dele — a
 * média é do período que está na tela, não da vida inteira da pessoa.
 *
 * Concluída SEM data não vira zero nem é somada como se fosse de hoje: fica
 * em `completedWithoutDate`, visível, fora da conta.
 */
export function dailyRateOf(
  lines: readonly DashboardFieldProgress[],
  filters: DashboardFilters = {},
  /** RDOs da pessoa no recorte. Ausente ou sem apontamento ⇒ derivado. */
  reported?: ReportedWork | undefined
): InspectorDailyRate {
  const completed = lines.filter(line => !line.outOfOperation && line.progressPercent >= 100);
  const days: string[] = [];
  let withoutDate = 0;
  for (const line of completed) {
    const day = dayOf(text(line.completedAt));
    if (!day) { withoutDate += 1; continue; }
    if (!withinPeriod(day, filters)) continue;
    days.push(day);
  }
  days.sort();
  const first = days[0] ?? null;
  const last = days[days.length - 1] ?? null;
  const completionDays = new Set(days);

  /**
   * O DENOMINADOR é a QUANTIDADE DE RDOs criados no período. Havendo
   * apontamento, ele manda — inclusive quando cobre mais dias que os de
   * conclusão, que é justamente o que o derivado não enxerga: dia em campo
   * sem Linha fechada.
   */
  const hasReported = reported !== undefined && reported.entries > 0;
  const daysSource = hasReported
    ? WorkedDaysSource.Rdo
    : completionDays.size > 0 ? WorkedDaysSource.Completions : WorkedDaysSource.None;

  const daysWorked = hasReported ? reported.entries : completionDays.size;
  const elapsedDays = first && last ? inclusiveDaySpan(first, last) : 0;
  const daysWithoutCompletion = hasReported
    ? [...reported.days].filter(day => !completionDays.has(day)).length
    : 0;

  return {
    completedWithDate: days.length,
    completedWithoutDate: withoutDate,
    daysWorked,
    daysSource,
    reportedEntries: hasReported ? reported.entries : 0,
    reportedDays: hasReported ? reported.days.size : 0,
    daysWithoutCompletion,
    hoursWorked: hasReported ? oneDecimal(reported.hours) : 0,
    elapsedDays,
    linesPerWorkedDay: daysWorked > 0 ? oneDecimal(days.length / daysWorked) : 0,
    linesPerElapsedDay: elapsedDays > 0 ? oneDecimal(days.length / elapsedDays) : 0,
    firstDay: first,
    lastDay: last,
    hasData: days.length > 0
  };
}

/**
 * Linhas DISTINTAS de um conjunto de Sistemas. A deduplicação é por
 * `lineEquipmentId`: a mesma Linha vinda duas vezes conta uma.
 */
function distinctLinesOf(
  fieldProgress: readonly DashboardFieldProgress[],
  systemIds: ReadonlySet<string>,
  directLineIds: ReadonlySet<string> = new Set()
): Map<string, DashboardFieldProgress> {
  const byLine = new Map<string, DashboardFieldProgress>();
  for (const line of fieldProgress) {
    const lineId = text(line.lineEquipmentId);
    if (!lineId || (!systemIds.has(text(line.systemId)) && !directLineIds.has(lineId))) continue;
    if (!byLine.has(lineId)) byLine.set(lineId, line);
  }
  return byLine;
}

/** Quantos Inspetores ATIVOS cada Sistema tem — base do escopo compartilhado. */
function inspectorsPerSystem(assignments: readonly InspectorAssignment[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const assignment of assignments) {
    if (!assignment.active) continue;
    const systemId = text(assignment.systemId);
    const person = text(assignment.inspectorProfessionalId);
    if (!systemId || !person) continue;
    const bucket = map.get(systemId);
    if (bucket) bucket.add(person);
    else map.set(systemId, new Set([person]));
  }
  return map;
}

export function computeInspectorPerformance(
  input: InspectorPerformanceInput,
  filters: DashboardFilters = {}
): InspectorPerformanceReport {
  const fieldProgress = input.fieldProgress.filter(line => matchesScope(line, filters));
  const inspections = (input.inspections ?? []).filter(inspection => matchesScope(inspection, filters));
  const sharing = inspectorsPerSystem(input.assignments);

  const inspectionsByPerson = new Map<string, InspectorInspection[]>();
  for (const inspection of inspections) {
    const person = text(inspection.inspectorProfessionalId);
    if (!person) continue;
    const bucket = inspectionsByPerson.get(person);
    if (bucket) bucket.push(inspection);
    else inspectionsByPerson.set(person, [inspection]);
  }

  const allLines = new Set<string>();
  const inspectors: InspectorPerformance[] = [];

  for (const person of input.professionals) {
    const professionalId = text(person.id);
    if (!professionalId) continue;

    const systemIds = systemsAssignedTo(professionalId, input.assignments, filters);
    const directLineIds = linesAssignedTo(professionalId, input.assignments, filters);
    const linesById = distinctLinesOf(fieldProgress, systemIds, directLineIds);
    const lines = [...linesById.values()];
    for (const lineId of linesById.keys()) allLines.add(lineId);

    const personInspections = inspectionsByPerson.get(professionalId) ?? [];
    // Sem vínculo de campo (Sistema OU Linha direta) E sem inspeção
    // atribuída não existe escopo individual: o bloco diz isso, em vez de
    // mostrar 0% de avanço. Uma Linha NOT_APPLICABLE vinculada diretamente
    // (sem Sistema) já é escopo — do contrário o Inspetor apareceria como
    // "NÃO CALCULÁVEL" com uma Linha corretamente contada em `totalLines`.
    const hasScope = systemIds.size > 0 || directLineIds.size > 0 || personInspections.length > 0;

    const systems: InspectorSystemBreakdown[] = [...systemIds]
      .map(systemId => {
        const systemLines = lines.filter(line => sameId(line.systemId, systemId));
        return {
          systemId,
          label: input.systemLabels?.get(systemId) ?? systemId,
          lines: systemLines.length,
          operational: systemLines.filter(line => !line.outOfOperation).length,
          completed: systemLines.filter(line => !line.outOfOperation && line.progressPercent >= 100).length,
          pending: systemLines.filter(line => !line.outOfOperation && line.progressPercent < 100).length,
          outOfOperation: systemLines.filter(line => line.outOfOperation).length,
          progressPercent: averageOf(systemLines),
          // POR SISTEMA o denominador é sempre o derivado: o RDO aponta o
          // dia da PESSOA, não o Sistema em que ela trabalhou naquele dia.
          // Ratear um dia de campo entre Sistemas seria inventar o rateio.
          dailyRate: dailyRateOf(systemLines, filters),
          shared: (sharing.get(systemId)?.size ?? 0) > 1,
          // REGRA V4-COMPLEMENTO: TAGs reais das linhas deste Sistema para drill-down.
          lineTags: systemLines.map(line => ({
            lineEquipmentId: text(line.lineEquipmentId),
            tag: input.lineLabels?.get(text(line.lineEquipmentId)) || text(line.lineEquipmentId),
            clientId: line.clientId ?? null,
            projectId: line.projectId ?? null,
            unitAreaId: line.unitAreaId ?? null,
            progressPercent: line.progressPercent,
            outOfOperation: line.outOfOperation
          }))
        };
      })
      .sort((a, b) =>
        b.dailyRate.linesPerWorkedDay - a.dailyRate.linesPerWorkedDay
        || b.progressPercent - a.progressPercent
        || a.label.localeCompare(b.label, "pt-BR"));

    // REGRA V4-COMPLEMENTO: Agrupador virtual OFF-SITE/TUBOVIA para linhas diretas
    // sem Sistema atribuídas ao Inspetor. Usa directLineIds já calculado e filtra
    // apenas linhas cujo systemId está ausente (não pertencem a nenhum Sistema real).
    if (directLineIds.size > 0) {
      const offsiteLines = lines.filter(line =>
        directLineIds.has(text(line.lineEquipmentId)) && !text(line.systemId)
      );
      if (offsiteLines.length > 0) {
        systems.push({
          systemId: OFFSITE_TUBOVIA_GROUP_ID,
          label: OFFSITE_TUBOVIA_GROUP_LABEL,
          lines: offsiteLines.length,
          operational: offsiteLines.filter(line => !line.outOfOperation).length,
          completed: offsiteLines.filter(line => !line.outOfOperation && line.progressPercent >= 100).length,
          pending: offsiteLines.filter(line => !line.outOfOperation && line.progressPercent < 100).length,
          outOfOperation: offsiteLines.filter(line => line.outOfOperation).length,
          progressPercent: averageOf(offsiteLines),
          dailyRate: dailyRateOf(offsiteLines, filters),
          shared: false,
          // TAGs REAIS das linhas OFF-SITE para drill-down no detalhamento.
          lineTags: offsiteLines.map(line => ({
            lineEquipmentId: text(line.lineEquipmentId),
            tag: input.lineLabels?.get(text(line.lineEquipmentId)) || text(line.lineEquipmentId),
            clientId: line.clientId ?? null,
            projectId: line.projectId ?? null,
            unitAreaId: line.unitAreaId ?? null,
            progressPercent: line.progressPercent,
            outOfOperation: line.outOfOperation
          }))
        });
      }
    }

    inspectors.push({
      professionalId,
      name: text(person.name) || professionalId,
      hasScope,
      unavailableReason: hasScope ? null : NO_INDIVIDUAL_SCOPE,
      assignedSystems: systemIds.size,
      totalLines: lines.length,
      operationalLines: lines.filter(line => !line.outOfOperation).length,
      completedLines: lines.filter(line => !line.outOfOperation && line.progressPercent >= 100).length,
      pendingLines: lines.filter(line => !line.outOfOperation && line.progressPercent < 100).length,
      outOfOperationLines: lines.filter(line => line.outOfOperation).length,
      progressPercent: averageOf(lines),
      dailyRate: dailyRateOf(lines, filters, input.workedDays?.reportedOf(professionalId)),
      sharedLines: lines.filter(line => (sharing.get(text(line.systemId))?.size ?? 0) > 1).length,
      assignedInspections: personInspections.length,
      completedInspections: personInspections.filter(i => COMPLETED_INSPECTION_STATUSES.has(text(i.status).toUpperCase())).length,
      systems
    });
  }

  /**
   * O ranking é POR RITMO: média de Linhas concluídas por dia trabalhado.
   *
   * O avanço percentual sozinho não ordena trabalho — quem recebeu 3 Linhas
   * e concluiu as 3 aparece à frente de quem concluiu 40 de 100, embora
   * tenha feito treze vezes menos. O percentual continua na tela, como
   * contexto do ritmo.
   *
   * Sem conclusão datada não há ritmo: essas pessoas vêm depois de quem tem,
   * ordenadas pelo avanço, e antes de quem não tem escopo nenhum. A lista
   * nunca abre por uma fileira de "não calculável".
   */
  inspectors.sort((a, b) =>
    Number(b.hasScope) - Number(a.hasScope)
    || Number(b.dailyRate.hasData) - Number(a.dailyRate.hasData)
    || b.dailyRate.linesPerWorkedDay - a.dailyRate.linesPerWorkedDay
    || b.progressPercent - a.progressPercent
    || a.name.localeCompare(b.name, "pt-BR"));

  return {
    hasData: inspectors.some(inspector => inspector.hasScope),
    inspectors,
    distinctLinesInScope: allLines.size,
    overlapping: [...sharing.values()].some(people => people.size > 1)
  };
}
